import type { MaintenanceCustomerListQuery } from '@gb-ticket/shared-types';
import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { usePageTitle } from '@/hooks/use-page-title';
import { PlatformPageHeader } from '@/platform/components/platform-page-header';
import {
  maintenanceCustomerListPath,
  maintenanceCustomerListQueryFromSearch,
} from '@/products/maintenance/features/maintenance-customer-list-path';
import { MaintenanceListFooter } from '@/products/maintenance/features/maintenance-list-footer';
import { MaintenanceOptionalSelect } from '@/products/maintenance/features/maintenance-filter-select';
import {
  formatMaintenanceListDate,
  maintenanceSearchFromPath,
  nextMaintenanceColumnSort,
} from '@/products/maintenance/features/maintenance-list-query';
import { MaintenanceListToolbar } from '@/products/maintenance/features/maintenance-list-toolbar';
import { MaintenanceRecordList } from '@/products/maintenance/features/maintenance-record-list';
import {
  MAINTENANCE_ACTIVE_FILTER_OPTIONS,
  maintenanceActiveFromSelect,
  maintenanceActiveSelectValue,
} from '@/products/maintenance/features/maintenance-status';
import { useMaintenanceCustomers } from '@/products/maintenance/features/use-maintenance';
import {
  maintenanceCustomerNewPath,
  maintenanceCustomerPath,
} from '@/products/maintenance/maintenance-paths';

const SEARCH_DEBOUNCE_MS = 300;

function isCustomerSortBy(
  value: string,
): value is NonNullable<MaintenanceCustomerListQuery['sortBy']> {
  return value === 'name' || value === 'customerNumber' || value === 'updatedAt';
}

function customerSearch(patch: Partial<MaintenanceCustomerListQuery>): string {
  return maintenanceSearchFromPath(maintenanceCustomerListPath(patch));
}

export function MaintenanceCustomersPage() {
  usePageTitle('Kunden');
  const [search, setSearch] = useSearchParams();
  const listQuery = useMemo(
    () => ({
      page: 1,
      pageSize: 25,
      sortBy: 'name' as const,
      sortOrder: 'asc' as const,
      ...maintenanceCustomerListQueryFromSearch(search.toString()),
    }),
    [search],
  );
  const customers = useMaintenanceCustomers(listQuery);
  const urlQ = listQuery.q ?? '';
  const [draft, setDraft] = useState(urlQ);
  const [draftSyncKey, setDraftSyncKey] = useState(urlQ);
  if (draftSyncKey !== urlQ) {
    setDraftSyncKey(urlQ);
    setDraft(urlQ);
  }

  function apply(patch: Partial<MaintenanceCustomerListQuery>) {
    setSearch(customerSearch({ ...listQuery, ...patch, page: patch.page ?? 1 }));
  }

  useEffect(() => {
    const handle = window.setTimeout(() => {
      const trimmed = draft.trim();
      const currentQ = listQuery.q ?? '';
      if (trimmed === currentQ) {
        return;
      }
      setSearch(customerSearch({ ...listQuery, q: trimmed || undefined, page: 1 }));
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [draft, listQuery, setSearch]);

  function applySearchNow() {
    setSearch(customerSearch({ ...listQuery, q: draft.trim() || undefined, page: 1 }));
  }

  function resetFilters() {
    setDraft('');
    setSearch(
      customerSearch({
        page: 1,
        pageSize: listQuery.pageSize,
        sortBy: 'name',
        sortOrder: 'asc',
      }),
    );
  }

  function onColumnSort(sortBy: string) {
    if (!isCustomerSortBy(sortBy)) {
      return;
    }
    const next = nextMaintenanceColumnSort(listQuery, sortBy);
    apply(next);
  }

  const filtered =
    Boolean(listQuery.q) ||
    listQuery.isActive !== undefined ||
    listQuery.sortBy !== 'name' ||
    listQuery.sortOrder !== 'asc';
  const items = customers.data?.items ?? [];

  return (
    <section className="min-w-0 space-y-6">
      <PlatformPageHeader
        title="Kunden"
        description="Stammdaten für Wartungsprojekte und Anlagen."
        action={
          <Button className="min-h-11" asChild>
            <Link to={maintenanceCustomerNewPath()}>Kunde anlegen</Link>
          </Button>
        }
      />

      <div className="space-y-3">
        <MaintenanceListToolbar filtered={filtered} onReset={resetFilters}>
          <div className="min-w-0 space-y-2">
            <Label htmlFor="cust-filter">Suche</Label>
            <Input
              id="cust-filter"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  applySearchNow();
                }
              }}
              placeholder="Name oder Nummer"
              className="min-h-11"
            />
          </div>
          <MaintenanceOptionalSelect
            id="cust-active"
            label="Aktiv"
            value={maintenanceActiveSelectValue(listQuery.isActive)}
            onValueChange={(value) => apply({ isActive: maintenanceActiveFromSelect(value) })}
            items={MAINTENANCE_ACTIVE_FILTER_OPTIONS}
          />
        </MaintenanceListToolbar>

        <MaintenanceRecordList
          rows={items}
          pending={customers.isPending ? 'Kunden werden geladen …' : undefined}
          error={customers.isError ? 'Die Kundenliste konnte nicht geladen werden.' : undefined}
          emptyLabel="Noch keine Kunden."
          filtered={filtered}
          onReset={resetFilters}
          getRowId={(row) => row.id}
          getHref={(row) => maintenanceCustomerPath(row.id)}
          getTitle={(row) => row.name}
          getMeta={(row) =>
            [row.customerNumber, row.address.city].filter(Boolean).join(' · ') || null
          }
          getStatus={(row) => row.isActive}
          titleSortKey="name"
          sort={{
            sortBy: listQuery.sortBy,
            sortOrder: listQuery.sortOrder,
            onSort: onColumnSort,
          }}
          columns={[
            {
              id: 'number',
              header: 'Nummer',
              sortKey: 'customerNumber',
              cell: (row) => row.customerNumber ?? '—',
            },
            {
              id: 'city',
              header: 'Ort',
              cell: (row) => row.address.city,
            },
            {
              id: 'updated',
              header: 'Geändert',
              sortKey: 'updatedAt',
              cell: (row) => formatMaintenanceListDate(row.updatedAt),
            },
          ]}
        />
        {items.length > 0 && customers.data ? (
          <MaintenanceListFooter
            page={customers.data.page}
            pageSize={customers.data.pageSize}
            total={customers.data.total}
            onPageChange={(page) => setSearch(customerSearch({ ...listQuery, page }))}
          />
        ) : null}
      </div>
    </section>
  );
}
